import { volatileSum } from "~/utils/PriceUtils";
import type { PriceData } from "~/domains/pricing/types/PriceData";
import type { LineItem } from "~/features/cart/cart/models/CartData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { isProductEntryFcc } from "~/features/cart/cart/utils/productEntryHelper";
import { selectTotalTargetPriceForPackage } from "~/features/cart/activeQuote/redux/selectors";
import { getVisibleNetPrice } from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { zeroNetPrice } from "~/utils/PriceUtils";
import type {
  QuoteEntryRowViewModel,
  QuotePackageEntryActions,
  QuotePackageEntryViewModel,
  QuoteTableMode,
  QuoteTableViewModel,
} from "~/features/cart/activeQuote/models/QuoteTableViewModel";

export interface PerEntrySelectionData {
  totalQuantity: number;
  subtotal: PriceData | null;
}

export interface ResolvedLineItemGroup {
  /** Unique id for this row (use the first lineItem's id as a stable key) */
  cartEntryId: number;
  lineItems: LineItem[];
}

export interface BuildQuoteTableViewModelArgs {
  mode: QuoteTableMode;
  resolvedGroups: ResolvedLineItemGroup[];
  perEntryData: Map<number, PerEntrySelectionData>;
  buildActions: (packEntryId: number) => QuotePackageEntryActions;
  buildCopyEntry: (group: ResolvedLineItemGroup) => () => void;
  getDescriptor: (group: ResolvedLineItemGroup) => ProductDescriptor;
}

function buildPackageEntryViewModel(
  lineItem: LineItem,
  product: ProductDescriptor,
  actions: QuotePackageEntryActions,
): QuotePackageEntryViewModel {
  const visiblePrice = getVisibleNetPrice(lineItem, product) ?? zeroNetPrice(product);
  const targetTotal = selectTotalTargetPriceForPackage(lineItem);

  return {
    packEntryId: lineItem.id,
    product,
    quantity: lineItem.quantity,
    packCode: lineItem.packCode,
    packSize: lineItem.packSize ?? 1,
    deliveryDate: lineItem.deliveryDate ? new Date(lineItem.deliveryDate) : null,
    comment: lineItem.comment ?? "",
    internalComment: lineItem.internalComment,
    showComments: lineItem.showComments,
    priceReference: lineItem.priceReference,
    offerPrice: lineItem.offerPrice,
    netPrice: lineItem.netPrice ?? undefined,
    rsp: lineItem.rsp,
    msp: lineItem.msp,
    targetPrice: lineItem.targetPrice,
    priceOverriden: lineItem.priceOverriden,
    isPackagingSelectionOpen: lineItem.isPackagingSelectionOpen,
    original: lineItem.original,
    valid: lineItem.valid,
    showCommentSection: lineItem.showComments,
    saleCampaignEnabled: lineItem.saleCampaignEnabled ?? false,
    quoteNumber: lineItem.quoteNumber,
    quoteLine: lineItem.quoteLine,
    messages: lineItem.messages,
    primaryWarehouseName: lineItem.primaryWarehouseName,
    source: lineItem.source,
    visiblePrice,
    targetTotal,
    actions,
  };
}

function buildEntryRowViewModel(
  group: ResolvedLineItemGroup,
  perEntryData: PerEntrySelectionData,
  buildActions: (packEntryId: number) => QuotePackageEntryActions,
  buildCopyEntry: (group: ResolvedLineItemGroup) => () => void,
  getDescriptor: (group: ResolvedLineItemGroup) => ProductDescriptor,
): QuoteEntryRowViewModel {
  const product = getDescriptor(group);
  const targetPerPack = group.lineItems.map(selectTotalTargetPriceForPackage);
  const targetSubtotal = targetPerPack.length > 0 ? volatileSum(...targetPerPack) : null;
  const isFcc = isProductEntryFcc(group.lineItems, product);

  const packageEntries: QuotePackageEntryViewModel[] = group.lineItems.map((lineItem) =>
    buildPackageEntryViewModel(lineItem, product, buildActions(lineItem.id)),
  );

  return {
    cartEntryId: group.cartEntryId,
    packEntryCount: group.lineItems.length,
    product,
    isFcc,
    totalQuantity: perEntryData.totalQuantity,
    subtotal: perEntryData.subtotal,
    targetSubtotal,
    packageEntries,
    copyEntry: buildCopyEntry(group),
  };
}

export function buildQuoteTableViewModel({
  mode,
  resolvedGroups,
  perEntryData,
  buildActions,
  buildCopyEntry,
  getDescriptor,
}: BuildQuoteTableViewModelArgs): QuoteTableViewModel {
  const entries = resolvedGroups.map((group) => {
    const entryData = perEntryData.get(group.cartEntryId) ?? { totalQuantity: 0, subtotal: null };
    return buildEntryRowViewModel(group, entryData, buildActions, buildCopyEntry, getDescriptor);
  });
  return { mode, entries };
}
